import React, { useState, useEffect } from 'react';
import {
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Divider,
    Collapse
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import { Link, useLocation } from 'react-router-dom';
import { trakrefBlue } from '../../color_utils';
import PieChartOutlineOutlinedIcon from '@mui/icons-material/PieChartOutlineOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import UserInfo from '../../UserInfo';
import FeatureFlags from '../../FeatureFlags';
import './SideNav.css';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import SpeedIcon from '@mui/icons-material/Speed';


import { useOnlineStatus, useIsDesktop } from "../../hooks";

// Bug #14972: previously imported `isDesktop` from utils.js, which is a
// module-level constant evaluated once at bundle load and never refreshed
// on resize/display-scaling change. Tenant admins who loaded the app
// while innerWidth ≤ 1279 (e.g. Windows scaling at 125%/150%, DevTools
// docked, snapped window) lost Compliance Center / Locations / Reports /
// BI Dashboard / Analytics until they triggered a full reload. Switched
// to the reactive `useIsDesktop()` hook (US 14628) so the nav re-computes
// on resize.

const SideNav = ({ closeMobileDrawer }) => {
    const isDesktop = useIsDesktop();
    const [navItems, setNavItems] = useState([]);
    const [inventoryOpen, setInventoryOpen] = useState(false);

    const listStyle = { marginTop: {xs: '10vh', md: '16vh', lg: '16vh'} };
    const listItemStyle = { padding: '8px 0px', '&:hover': { textDecoration: 'none' } }
    const textStyle = {
        '& .MuiListItemText-primary': { fontWeight: 600, fontSize: '12px' },
        fontFamily: 'Open Sans',
        color: '#000',
    };
    const activePageIndicatorStyle = {
        "& .MuiButtonBase-root.MuiListItemButton-root": {
            borderLeft: `4px solid ${trakrefBlue}`,
        },
        "& .MuiListItemIcon-root.MuiListItemIcon-root": {
            marginLeft: "-4px"
        }
    };
    const iconWidth = { '&.MuiListItemIcon-root': { minWidth: '35px' } };
    const iconColor = { color: trakrefBlue };
    const location = useLocation();
    const isOnline = useOnlineStatus();

    // show command center for these
    // is system admin || is super tenant admin || is tenant admin

    // SHOW BI DASHBOARD FOR THESE CONDITIONS
    // if user is ready only
    // || if you have permission for the current instance you are viewing (native) and are a native super tenant, native tenant, or sys admin
    // && instance.PowerBIWorkSpaceID.value


    // WORKFLOW - get to see workflow page
    // if system admin || super tenant admin || tenant admin

    // check ServicerInstanc table in database
    // CanManageLocations
    // RestrictedLocations

    const handleInventoryToggle = () => {
        setInventoryOpen(prev => !prev);
    };

    const determineSelectedSideNav = (item) => {
        const { similarPath = null } = item;
        const current_location = location.pathname;

        if (current_location === item.to || current_location.includes(similarPath)) {
            return true;
        }

        if (item.subMenu) {
            return item.subMenu.some(sub => sub.to === current_location);
        }

        return false;
    };

    const determineNavItems = () => {
        // Base items shown alongside the conditional ones below. These were
        // previously stored in component state and merged in via spread,
        // which made the result depend on whatever `isDesktop` was at first
        // mount (bug #14972). Defining them inline here so the entire list
        // is recomputed against the current viewport every time this runs.
        const baseNavItems = [
            { to: "/WorkOrders", similarPath: "WorkOrder", icon: <AssignmentTurnedInOutlinedIcon />, text: "Work Orders" },
            { to: "/ServiceEvents", similarPath: "ServiceEvent", icon: <DescriptionOutlinedIcon />, text: "Service Events" },
            // US 14659: Locations appears in the nav at every breakpoint (mobile/
            // tablet/desktop), matching Inventory below. Previously desktop-only.
            { to: "/Locations", similarPath: "Location", icon: <RoomOutlinedIcon />, text: "Locations" },
            {
                to: "/Inventory", similarPath: "Inventory", icon: <PlaylistAddCheckOutlinedIcon />, text: "Inventory",
                subMenu: [
                    { title: "Inventory", to: "/Inventory", isParent: true },
                    { title: "Material Transfers", to: "/MaterialTransfers", isParent: false },
                    { title: "Appliance Disposals", to: "/ApplianceDisposals", isParent: false },
                ],
            },
            ...(isDesktop ? [{ to: "/TrakrefReports", icon: <PieChartOutlineOutlinedIcon />, text: "Reports" }] : []),
        ];

        const newNavItems = [];
        let commandcenterRoute = { to: "/Home", icon: <HomeOutlinedIcon />, text: "Home" };

        if (UserInfo.isServiceProvider()) { // service provider
            newNavItems.push(commandcenterRoute);
        }
        else { // other users
            if (UserInfo.isPurchasingAdministrator() || UserInfo.isSystemAdministrator() || UserInfo.isTechnician() || UserInfo.isTechnicianManager()) {
                newNavItems.push(commandcenterRoute);
            }

            if (isDesktop && !UserInfo.isNativeAccountServiceProvider() && (UserInfo.isReadOnly() || UserInfo.isSystemAdministrator() || UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator() || UserInfo.isRegionalAdministrator())) {
                const commandcenterRoute = { to: "/ComplianceCenter", icon: <FactCheckIcon />, text: "Compliance Center" };
                const dashboardRoute = { to: "/Dashboard", icon: <SpeedIcon />, text: "Dashboard" };
                if (FeatureFlags.isEnabled('LeakInspectionsDashboard')) {
                    newNavItems.push(dashboardRoute);
                }
                newNavItems.push(commandcenterRoute);
            }

            newNavItems.push(...baseNavItems);

            // If user is non-native service provider (tenant admin/tech manager), remove TrakrefReports
            if (isDesktop && !UserInfo.isNativeUser() && !UserInfo.isTenantAdministrator() && !UserInfo.isTechnicianManager()) {
                newNavItems.pop();
            }

            if (isDesktop && (UserInfo.isSystemAdministrator() || (
                UserInfo.isNativeSystemOwner() &&
                (
                    UserInfo.isSuperTenantAdmin() || 
                    UserInfo.isRegionalAdministrator() || 
                    UserInfo.isReadOnly() || 
                    UserInfo.isSustainabilityManager() || 
                    UserInfo.isTenantAdministrator()
                ))))
            {
                const biDashboardRoute = { to: "/Reports", icon: <AssessmentOutlinedIcon />, text: "BI Dashboard" };
                newNavItems.push(biDashboardRoute);
            }
             if (
                UserInfo.isAdminRole()
                || UserInfo.isRegionalAdministrator()
                || UserInfo.isReadOnly()
                || UserInfo.isSustainabilityManager()
            ) {
                // const workFlowRoute = { to: "/Workflow", icon: <ViewWeekOutlinedIcon />, text: "Workflow" };
                if (isDesktop && UserInfo.allowAnalytics && (UserInfo.isSystemAdministrator() || UserInfo.isNativeSystemOwner())) {
                    const analyticRoute = { to: "/Analytics", icon: <ArticleOutlinedIcon />, text: "Analytics" };
                    newNavItems.push(analyticRoute);
                }
                // newNavItems.push(workFlowRoute);
                if (isDesktop && !UserInfo.isProduction) {
                    const insightsRoute = { to: "/Insights", icon: <LightbulbOutlinedIcon />, text: "Insights" };
                    newNavItems.push(insightsRoute);
                }
            }
        }

        return newNavItems;
    };

    useEffect(() => {
        setNavItems(determineNavItems());
    }, [isDesktop]);

    useEffect(() => {
        const inventoryItem = navItems.find(item => item.subMenu);
        if (inventoryItem) {
            const onSubRoute = inventoryItem.subMenu.some(
                sub => sub.to === location.pathname
            );
            if (onSubRoute) {
                setInventoryOpen(true);
            }
        }
    }, [location.pathname]);

    return (
        <>
            <List sx={listStyle} className={isOnline ? 'sideNavDemo' : 'sideNavDemo offlineOverlay'} component="nav">
                {navItems.map(item => (
                    <React.Fragment key={item.text}>
                        {item.subMenu ? (
                            <>
                                <ListItem
                                    sx={determineSelectedSideNav(item) ? { ...listItemStyle, ...activePageIndicatorStyle } : listItemStyle}
                                >
                                    <ListItemButton onClick={handleInventoryToggle}>
                                        <ListItemIcon sx={determineSelectedSideNav(item) ? { ...iconWidth, ...iconColor } : iconWidth}>
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText primary={item.text} sx={textStyle} />
                                        {inventoryOpen ? <ExpandLess /> : <ExpandMore />}
                                    </ListItemButton>
                                </ListItem>
                                <Collapse in={inventoryOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.subMenu.map(subItem => (
                                            <ListItemButton
                                                key={subItem.title}
                                                component={Link}
                                                to={subItem.to}
                                                onClick={() => closeMobileDrawer && closeMobileDrawer()}
                                                sx={{
                                                    paddingLeft: '52px',
                                                    borderLeft: `3px solid ${location.pathname === subItem.to ? trakrefBlue : 'transparent'}`,
                                                    '&:hover': { textDecoration: 'none' },
                                                }}
                                            >
                                                <ListItemText primary={subItem.title} sx={textStyle} />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                </Collapse>
                            </>
                        ) : (
                            <ListItem
                                component={Link}
                                to={item.to}
                                onClick={() => closeMobileDrawer && closeMobileDrawer()}
                                sx={determineSelectedSideNav(item) ? { ...listItemStyle, ...activePageIndicatorStyle } : listItemStyle}
                            >
                                <ListItemButton>
                                    <ListItemIcon sx={determineSelectedSideNav(item) ? { ...iconWidth, ...iconColor } : iconWidth}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText primary={item.text} sx={textStyle} />
                                </ListItemButton>
                            </ListItem>
                        )}
                    </React.Fragment>
                ))}
            </List>

            <Divider />
        </>
    );
};

export default SideNav;
